import { PollCreate } from '@wellesley/openapi'
import { UseFormReturn } from 'react-hook-form'
import { FC, useEffect, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import PollItemRow from './PollItemRow'

type OptionsProps = {
  options: string[]
  form: UseFormReturn<PollCreate>
  handleRemoveOption: (index: number) => void
}

const Options: FC<OptionsProps> = ({ options, form, handleRemoveOption }) => {
  const ref = useRef<HTMLDivElement>(null)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    const firstOption = ref.current?.querySelector(
      'input[name="options.0"]',
    ) as HTMLInputElement

    firstOption?.focus()
  }, [])

  const saveOption = (index: number, value: string) => {
    form.setValue(`options.${index}`, value, {
      shouldDirty: true,
    })
  }

  const saveOptions = (options: string[]) => {
    form.setValue('options', options, {
      shouldDirty: true,
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = options.indexOf(active.id as string)
      const newIndex = options.indexOf(over?.id as string)

      const newArray = arrayMove(options, oldIndex, newIndex)

      saveOptions(newArray)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={options} strategy={verticalListSortingStrategy}>
        <div ref={ref} className="relative flex flex-col">
          {options.map((value, index) => (
            <PollItemRow
              key={`${index}`}
              value={value}
              name={`options.${index}`}
              control={form.control}
              placeholder={`Choice ${index + 1}`}
              disabledDeleteButton={options.length <= 2}
              onRemove={() => handleRemoveOption(index)}
              onChange={(value: string) => saveOption(index, value)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default Options
